import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import {
  WebSocketState,
  WebSocketConnectionStatus,
  WebSocketMessage,
  WebSocketEvent,
  AsyncThunkConfig
} from '../../types/state';

// =============================================================================
// WebSocket Service
// =============================================================================

class WebSocketService {
  private socket: WebSocket | null = null;
  private reconnectTimer: NodeJS.Timeout | null = null;
  private pingTimer: NodeJS.Timeout | null = null;
  private dispatch: any = null;
  private messageQueue: WebSocketMessage[] = [];
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectInterval = 1000; // Start with 1 second
  private maxReconnectInterval = 30000; // Max 30 seconds

  setDispatch(dispatch: any) {
    this.dispatch = dispatch;
  }

  connect(url: string, protocols?: string[]): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        this.socket = new WebSocket(url, protocols);
        
        this.socket.onopen = () => {
          console.log('[WebSocket] Connected to server');
          this.reconnectAttempts = 0;
          this.reconnectInterval = 1000;
          this.startPing();
          this.processMessageQueue();
          this.dispatch?.(setConnectionStatus(WebSocketConnectionStatus.CONNECTED));
          resolve();
        };

        this.socket.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            this.handleMessage(data);
          } catch (error) {
            console.error('[WebSocket] Error parsing message:', error);
          }
        };

        this.socket.onclose = (event) => {
          console.log('[WebSocket] Connection closed:', event.code, event.reason);
          this.stopPing();
          
          if (!event.wasClean && this.reconnectAttempts < this.maxReconnectAttempts) {
            this.dispatch?.(setConnectionStatus(WebSocketConnectionStatus.RECONNECTING));
            this.scheduleReconnect(url, protocols);
          } else {
            this.dispatch?.(setConnectionStatus(WebSocketConnectionStatus.DISCONNECTED));
          }
        };

        this.socket.onerror = (error) => {
          console.error('[WebSocket] Connection error:', error);
          this.dispatch?.(setError('WebSocket connection error'));
          reject(error);
        };

      } catch (error) {
        reject(error);
      }
    });
  }

  disconnect() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    
    this.stopPing();
    
    if (this.socket) {
      this.socket.close(1000, 'Client disconnect');
      this.socket = null;
    }
    
    this.dispatch?.(setConnectionStatus(WebSocketConnectionStatus.DISCONNECTED));
  }

  send(message: WebSocketMessage): boolean {
    if (this.socket?.readyState === WebSocket.OPEN) {
      try {
        this.socket.send(JSON.stringify(message));
        this.dispatch?.(markMessageAcknowledged(message.id));
        return true;
      } catch (error) {
        console.error('[WebSocket] Error sending message:', error);
        this.queueMessage(message);
        return false;
      }
    } else {
      this.queueMessage(message);
      return false;
    }
  }

  joinChannel(channel: string) {
    const message: WebSocketMessage = {
      id: this.generateId(),
      type: 'join_channel',
      payload: { channel },
      timestamp: new Date().toISOString(),
      acknowledged: false,
      retryCount: 0
    };
    
    this.send(message);
    this.dispatch?.(addActiveChannel(channel));
  }

  leaveChannel(channel: string) {
    const message: WebSocketMessage = {
      id: this.generateId(),
      type: 'leave_channel',
      payload: { channel },
      timestamp: new Date().toISOString(),
      acknowledged: false,
      retryCount: 0
    };
    
    this.send(message);
    this.dispatch?.(removeActiveChannel(channel));
  }

  private handleMessage(data: any) {
    const event: WebSocketEvent = {
      id: this.generateId(),
      type: data.type,
      data: data.payload || data,
      timestamp: new Date().toISOString(),
      processed: false
    };

    this.dispatch?.(addEvent(event));

    // Handle specific message types
    switch (data.type) {
      case 'pong':
        this.dispatch?.(updateLastPing(new Date().toISOString()));
        break;
      case 'project_update':
        this.handleProjectUpdate(data.payload);
        break;
      case 'milestone_completed':
        this.handleMilestoneUpdate(data.payload);
        break;
      case 'payment_received':
        this.handlePaymentUpdate(data.payload);
        break;
      case 'message_received':
        this.handleNewMessage(data.payload);
        break;
      case 'notification':
        this.handleNotification(data.payload);
        break;
      default:
        console.log('[WebSocket] Unhandled message type:', data.type);
    }
  }

  private handleProjectUpdate(payload: any) {
    // Dispatch to project slice
    console.log('[WebSocket] Project update received:', payload);
    // This would trigger a refresh of project data
  }

  private handleMilestoneUpdate(payload: any) {
    console.log('[WebSocket] Milestone update received:', payload);
    // This would update milestone status
  }

  private handlePaymentUpdate(payload: any) {
    console.log('[WebSocket] Payment update received:', payload);
    // This would update payment status
  }

  private handleNewMessage(payload: any) {
    console.log('[WebSocket] New message received:', payload);
    // This would add message to messages slice
  }

  private handleNotification(payload: any) {
    console.log('[WebSocket] Notification received:', payload);
    // This would add notification to notifications slice
  }

  private scheduleReconnect(url: string, protocols?: string[]) {
    this.reconnectAttempts++;
    
    // Exponential backoff with jitter
    const backoffDelay = Math.min(
      this.reconnectInterval * Math.pow(2, this.reconnectAttempts - 1),
      this.maxReconnectInterval
    );
    const jitter = Math.random() * 1000; // Add up to 1 second of jitter
    const delay = backoffDelay + jitter;

    console.log(`[WebSocket] Reconnecting in ${Math.round(delay)}ms (attempt ${this.reconnectAttempts})`);

    this.reconnectTimer = setTimeout(() => {
      this.connect(url, protocols).catch(() => {
        // If reconnection fails, try again
        if (this.reconnectAttempts < this.maxReconnectAttempts) {
          this.scheduleReconnect(url, protocols);
        } else {
          this.dispatch?.(setConnectionStatus(WebSocketConnectionStatus.ERROR));
          this.dispatch?.(setError('Max reconnection attempts exceeded'));
        }
      });
    }, delay);
  }

  private startPing() {
    this.pingTimer = setInterval(() => {
      if (this.socket?.readyState === WebSocket.OPEN) {
        const pingMessage: WebSocketMessage = {
          id: this.generateId(),
          type: 'ping',
          payload: { timestamp: Date.now() },
          timestamp: new Date().toISOString(),
          acknowledged: false,
          retryCount: 0
        };
        this.send(pingMessage);
      }
    }, 30000); // Ping every 30 seconds
  }

  private stopPing() {
    if (this.pingTimer) {
      clearInterval(this.pingTimer);
      this.pingTimer = null;
    }
  }

  private queueMessage(message: WebSocketMessage) {
    this.messageQueue.push(message);
    this.dispatch?.(queueMessage(message));
    
    // Retry failed messages
    if (message.retryCount < 3) {
      setTimeout(() => {
        if (this.socket?.readyState === WebSocket.OPEN) {
          message.retryCount++;
          this.send(message);
        }
      }, 1000 * (message.retryCount + 1));
    }
  }

  private processMessageQueue() {
    while (this.messageQueue.length > 0 && this.socket?.readyState === WebSocket.OPEN) {
      const message = this.messageQueue.shift();
      if (message) {
        this.send(message);
      }
    }
  }

  private generateId(): string {
    return Math.random().toString(36).substring(2) + Date.now().toString(36);
  }

  getConnectionState(): WebSocketConnectionStatus {
    if (!this.socket) return WebSocketConnectionStatus.DISCONNECTED;
    
    switch (this.socket.readyState) {
      case WebSocket.CONNECTING:
        return WebSocketConnectionStatus.CONNECTING;
      case WebSocket.OPEN:
        return WebSocketConnectionStatus.CONNECTED;
      case WebSocket.CLOSING:
      case WebSocket.CLOSED:
        return WebSocketConnectionStatus.DISCONNECTED;
      default:
        return WebSocketConnectionStatus.ERROR;
    }
  }
}

// Global WebSocket service instance
export const webSocketService = new WebSocketService();

// =============================================================================
// Initial State
// =============================================================================

const initialState: WebSocketState = {
  connectionStatus: WebSocketConnectionStatus.DISCONNECTED,
  isConnected: false,
  lastPing: null,
  reconnectAttempts: 0,
  maxReconnectAttempts: 5,
  reconnectInterval: 1000,
  activeChannels: [],
  messageQueue: [],
  events: [],
  error: null,
};

// =============================================================================
// Async Thunks
// =============================================================================

export const connectWebSocket = createAsyncThunk<
  void,
  { url: string; protocols?: string[] },
  AsyncThunkConfig
>(
  'realTime/connect',
  async ({ url, protocols }, { dispatch, rejectWithValue }) => {
    try {
      webSocketService.setDispatch(dispatch);
      await webSocketService.connect(url, protocols);
      return; // Explicitly return void
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to connect to WebSocket');
    }
  }
);

export const disconnectWebSocket = createAsyncThunk<
  void,
  void,
  AsyncThunkConfig
>(
  'realTime/disconnect',
  async (_, { rejectWithValue }) => {
    try {
      webSocketService.disconnect();
      return; // Explicitly return void
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to disconnect WebSocket');
    }
  }
);

export const sendMessage = createAsyncThunk<
  void,
  { type: string; payload: any },
  AsyncThunkConfig
>(
  'realTime/sendMessage',
  async ({ type, payload }, { rejectWithValue }) => {
    try {
      const message: WebSocketMessage = {
        id: Math.random().toString(36).substring(2) + Date.now().toString(36),
        type,
        payload,
        timestamp: new Date().toISOString(),
        acknowledged: false,
        retryCount: 0
      };

      const sent = webSocketService.send(message);
      if (!sent) {
        throw new Error('Failed to send message - connection not available');
      }
      return; // Explicitly return void
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to send message');
    }
  }
);

export const joinChannel = createAsyncThunk<
  void,
  string,
  AsyncThunkConfig
>(
  'realTime/joinChannel',
  async (channel, { rejectWithValue }) => {
    try {
      webSocketService.joinChannel(channel);
      return; // Explicitly return void
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to join channel');
    }
  }
);

export const leaveChannel = createAsyncThunk<
  void,
  string,
  AsyncThunkConfig
>(
  'realTime/leaveChannel',
  async (channel, { rejectWithValue }) => {
    try {
      webSocketService.leaveChannel(channel);
      return; // Explicitly return void
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to leave channel');
    }
  }
);

// =============================================================================
// Real-time Slice
// =============================================================================

const realTimeSlice = createSlice({
  name: 'realTime',
  initialState,
  reducers: {
    setConnectionStatus: (state, action: PayloadAction<WebSocketConnectionStatus>) => {
      state.connectionStatus = action.payload;
      state.isConnected = action.payload === WebSocketConnectionStatus.CONNECTED;
    },

    updateLastPing: (state, action: PayloadAction<string>) => {
      state.lastPing = action.payload;
    },

    incrementReconnectAttempts: (state) => {
      state.reconnectAttempts += 1;
    },

    resetReconnectAttempts: (state) => {
      state.reconnectAttempts = 0;
    },

    addActiveChannel: (state, action: PayloadAction<string>) => {
      if (!state.activeChannels.includes(action.payload)) {
        state.activeChannels.push(action.payload);
      }
    },

    removeActiveChannel: (state, action: PayloadAction<string>) => {
      state.activeChannels = state.activeChannels.filter(
        channel => channel !== action.payload
      );
    },

    queueMessage: (state, action: PayloadAction<WebSocketMessage>) => {
      state.messageQueue.push(action.payload);
    },

    markMessageAcknowledged: (state, action: PayloadAction<string>) => {
      const message = state.messageQueue.find(msg => msg.id === action.payload);
      if (message) {
        message.acknowledged = true;
      }
    },

    removeAcknowledgedMessages: (state) => {
      state.messageQueue = state.messageQueue.filter(msg => !msg.acknowledged);
    },

    addEvent: (state, action: PayloadAction<WebSocketEvent>) => {
      state.events.unshift(action.payload);
      // Keep only the last 100 events
      if (state.events.length > 100) {
        state.events = state.events.slice(0, 100);
      }
    },

    markEventProcessed: (state, action: PayloadAction<string>) => {
      const event = state.events.find(evt => evt.id === action.payload);
      if (event) {
        event.processed = true;
      }
    },

    clearEvents: (state) => {
      state.events = [];
    },

    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },

    clearError: (state) => {
      state.error = null;
    },

    updateReconnectInterval: (state, action: PayloadAction<number>) => {
      state.reconnectInterval = action.payload;
    },
  },

  extraReducers: (builder) => {
    builder
      // Connect
      .addCase(connectWebSocket.pending, (state) => {
        state.connectionStatus = WebSocketConnectionStatus.CONNECTING;
        state.isConnected = false;
        state.error = null;
      })
      .addCase(connectWebSocket.fulfilled, (state) => {
        state.connectionStatus = WebSocketConnectionStatus.CONNECTED;
        state.isConnected = true;
        state.error = null;
        state.reconnectAttempts = 0;
      })
      .addCase(connectWebSocket.rejected, (state, action) => {
        state.connectionStatus = WebSocketConnectionStatus.ERROR;
        state.isConnected = false;
        state.error = action.payload || 'Connection failed';
      })

      // Disconnect
      .addCase(disconnectWebSocket.fulfilled, (state) => {
        state.connectionStatus = WebSocketConnectionStatus.DISCONNECTED;
        state.isConnected = false;
        state.activeChannels = [];
        state.messageQueue = [];
        state.error = null;
      })

      // Send message
      .addCase(sendMessage.rejected, (state, action) => {
        state.error = action.payload || 'Failed to send message';
      });
  },
});

// =============================================================================
// Selectors
// =============================================================================

export const selectRealTime = (state: { realTime: WebSocketState }) => state.realTime;
export const selectConnectionStatus = (state: { realTime: WebSocketState }) => 
  state.realTime.connectionStatus;
export const selectIsConnected = (state: { realTime: WebSocketState }) => 
  state.realTime.isConnected;
export const selectActiveChannels = (state: { realTime: WebSocketState }) => 
  state.realTime.activeChannels;
export const selectMessageQueue = (state: { realTime: WebSocketState }) => 
  state.realTime.messageQueue;
export const selectRecentEvents = (state: { realTime: WebSocketState }) => 
  state.realTime.events.slice(0, 10);
export const selectUnprocessedEvents = (state: { realTime: WebSocketState }) => 
  state.realTime.events.filter(event => !event.processed);
export const selectWebSocketError = (state: { realTime: WebSocketState }) => 
  state.realTime.error;

// =============================================================================
// Actions Export
// =============================================================================

export const {
  setConnectionStatus,
  updateLastPing,
  incrementReconnectAttempts,
  resetReconnectAttempts,
  addActiveChannel,
  removeActiveChannel,
  queueMessage,
  markMessageAcknowledged,
  removeAcknowledgedMessages,
  addEvent,
  markEventProcessed,
  clearEvents,
  setError,
  clearError,
  updateReconnectInterval,
} = realTimeSlice.actions;

export default realTimeSlice.reducer;